import { useMemo, type FC } from "react";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { effectiveState } from "@/routes/environments/phase";
import { statusText } from "@/utils/environment";
import { Text } from "@/components/podkit/typography/Text";
import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useListEditors } from "@/queries/editor-queries";
import { EnvironmentActionsDropdown } from "@/routes/environments/details/EnvironmentActionsDropdown";

export const AgentExecutionActiveEnvironment: FC<{ agentExecution: PlainAgentExecution }> = ({ agentExecution }) => {
    const environmentId = agentExecution.status?.usedEnvironments[0]?.environmentId;
    const { data: environment } = useEnvironment(environmentId);

    const { editorId } = useGetEditorForEnv(environmentId);
    const { data: editors } = useListEditors();
    const selectedEditor = editors?.editors.find((editor) => editor.id === editorId) || editors?.editors[0];

    if (!environment) {
        return null;
    }

    return (
        <EnvironmentActionsDropdown
            environment={environment}
            defaultEditor={selectedEditor}
            button={
                <div className="flex h-6 select-none items-center gap-1 overflow-hidden rounded-[4px] border-0.5 border-border-base bg-surface-02 py-1 pl-1 pr-2.5 hover:cursor-pointer">
                    <EnvironmentStatusDot className="shrink-0" size="base" env={environment} />
                    <EnvironmentStatus env={environment} />
                </div>
            }
        />
    );
};

const EnvironmentStatus: FC<{ env: PlainEnvironment }> = ({ env }) => {
    const state = useMemo(() => effectiveState(env), [env]);
    const text = useMemo(() => statusText(state), [state]);

    return <Text className="min-w-0 shrink truncate text-sm font-bold text-content-secondary">Environment {text}</Text>;
};
